<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title>
            <v-flex>
              Athletes Associated with Email:
              <v-text-field
                v-model="email"
                label="Enter Email"
                outlined
                dense
              ></v-text-field>
            </v-flex>
            <v-divider vertical class="mx-1" inset></v-divider>
            <v-flex>
              <v-btn color="primary" @click="setManualEmail"
                >Fetch Athletes</v-btn
              >
            </v-flex>
          </v-card-title>
          <v-card-text>
            <v-alert
              v-if="!athleteList.edges.length && !loading"
              type="info"
              dense
              outlined
            >
              No athletes found for the provided email.
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="athleteList.edges"
              item-value="id"
              class="elevation-1"
              dense
              :sort-by="'node.team.competitiveFlag'"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title
                    >Please confirm athlete participation in upcoming Club
                    Term</v-toolbar-title
                  >
                  <v-spacer></v-spacer>
                  <v-progress-circular
                    v-if="loading"
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </v-toolbar>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  class="white--text"
                >
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:[`item.node.team.teamName`]="{ item }">
                <div>
                  <td>
                    {{
                      item.node.team.teamName +
                      (item.node.team.competitiveFlag
                        ? " (competitive)"
                        : " (non-competitive)")
                    }}
                  </td>
                </div>
              </template>
              <template v-slot:[`item.node.team.webpage`]="{ item }">
                <div>
                  <td>
                    <a
                      :href="item.node.team.webpage"
                      target="_blank"
                      rel="noopener noreferrer"
                      style="
                        font-size: 0.85em;
                        color: #007bff;
                        text-decoration: underline;
                      "
                    >
                      {{ truncateUrl(item.node.team.webpage) }}
                    </a>
                  </td>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span>
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        :disabled="item.node.team.competitiveFlag === 1"
                        small
                        color="green"
                        @click="openConfirmDialog(item)"
                      >
                        Confirm
                      </v-btn>
                    </span>
                  </template>
                  <span v-if="item.node.team.competitiveFlag === 1">
                    Action disabled due to competitive team flag.
                  </span>
                  <span v-else
                    >Click to confirm athlete participation in upcoming
                    term</span
                  >
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" style="display: inline-block">
                      <v-btn
                        :disabled="item.node.team.competitiveFlag === 1"
                        small
                        color="red"
                        @click="openDeclineDialog(item)"
                      >
                        Decline
                      </v-btn>
                    </div>
                  </template>
                  <span v-if="item.node.team.competitiveFlag === 1">
                    Action disabled due to competitive team flag.
                  </span>
                  <span v-else>Click to decline the athlete.</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogs are the same as in the earlier example -->
    <v-dialog v-model="declineDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Reason for Decline</span>
        </v-card-title>

        <v-card-text>
          <v-textarea
            v-model="additionalInfo"
            label="As we are constantly working on improvement of our service, 
            Please provide the reason for declining"
            outlined
            rows="3"
            required
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" text @click="closeDeclineDialog">Cancel</v-btn>
          <v-btn color="red" text @click="confirmDecline">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Confirmation Feedback -->
    <v-dialog v-model="confirmDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Advice for Service Improvement</span>
        </v-card-title>

        <v-card-text>
          <v-textarea
            v-model="serviceFeedback"
            label="We value your feedback! Please share advice for our service improvement."
            outlined
            rows="3"
            required
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="closeConfirmDialog">Cancel</v-btn>
          <v-btn color="green" text @click="confirmParticipation">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      email: null,
      manualEmail: "",
      headers: [
        { text: "First Name", value: "node.firstName" },
        { text: "Last Name", value: "node.lastName" },
        { text: "ClubName", value: "node.club.clubName" },
        { text: "Qualified Group", value: "node.team.teamName" },
        { text: "Group Info", value: "node.team.webpage" },
        { text: "Status", value: "node.status" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      loading: false,
      declineDialog: false, // Controls the decline dialog
      confirmDialog: false, // Controls the confirmation dialog
      additionalInfo: "", // Stores reason for decline
      serviceFeedback: "", // Stores feedback for confirmation
      selectedItem: null, // Stores the selected item
    };
  },
  computed: {
    ...mapGetters(["athleteList"]),
  },
  created() {
    this.email = this.$route.query.email || "Please provide email"; // Get email from query string
    if (this.email) {
      this.fetchAthletes();
    }
  },
  methods: {
    ...mapActions(["addUpdateAthlete", "allAthlete"]),
    async fetchAthletes() {
      this.loading = true;
      await this.allAthlete({ email: this.email })
        .then(() => (this.loading = false))
        .catch((error) => {
          this.loading = false;
          console.error(error);
        });
    },
    setManualEmail() {
      if (this.email.trim()) {
        this.email = this.email.trim();
        this.$router.replace({
          query: { ...this.$route.query, email: this.email },
        });
        this.fetchAthletes();
      }
    },
    truncateUrl(url) {
      if (!url) return "";
      return url.length > 20 ? `${url.slice(0, 17)}...` : url;
    },
    openDeclineDialog(item) {
      this.selectedItem = item; // Store the selected item
      this.declineDialog = true; // Open the decline dialog
    },
    closeDeclineDialog() {
      this.declineDialog = false; // Close the decline dialog
      this.additionalInfo = ""; // Reset the input field
    },
    confirmDecline() {
      if (!this.additionalInfo.trim()) {
        this.$toast.error("Please provide a reason before submitting.");
        return;
      }
      this.updateStatus(this.selectedItem, "declined", this.additionalInfo);
      this.closeDeclineDialog();
    },
    openConfirmDialog(item) {
      this.selectedItem = item; // Store the selected item
      this.confirmDialog = true; // Open the confirmation dialog
    },
    closeConfirmDialog() {
      this.confirmDialog = false; // Close the confirmation dialog
      this.serviceFeedback = ""; // Reset the input field
    },
    confirmParticipation() {
      if (!this.serviceFeedback.trim()) {
        this.$toast.error("Please provide feedback before submitting.");
        return;
      }
      this.updateStatus(this.selectedItem, "confirmed", this.serviceFeedback);
      this.closeConfirmDialog();
    },
    async updateStatus(item, status, reasonOrFeedback) {
      item.status = status;
      await this.addUpdateAthlete({
        athleteData: {
          //...item.node,
          status: status,
          firstName: String(item.node.firstName),
          lastName: String(item.node.lastName),
          email: String(item.node.email),
          startMembership: String(item.node.startMembership),
          clubId: item.node.clubId,
          teamId: item.node.teamId,
          gender: item.node.gender,
          birthDate: item.node.birthDate,
          sourceOfInformation: item.node.sourceOfInformation,
          participationDays: item.node.participationDays,
          additionalInfo:
            `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
              .toString()
              .padStart(2, "0")}-${new Date()
              .getDate()
              .toString()
              .padStart(2, "0")}` +
            " " +
            reasonOrFeedback +
            " | " +
            item.node.additionalInfo, // Include feedback or reason
          personId: item.athleteId,
          updateSource: this.email,
        },
      })
        .then(() => {
          try {
            this.loading = false;
            console.log("Success: Athlete added");
            this.$toast.success(
              `Athlete ${item.node.firstName} ${item.node.lastName} has been added for tryout, we will contact you on next steps.`,
              { position: "bottom-center", duration: 30000 }
            );
          } catch (err) {
            console.error("Error during success handling:", err);
            this.$toast.error(
              "An unexpected error occurred during success handling.",
              {
                position: "bottom-center",
                duration: 30000,
              }
            );
          }
        })
        .catch((err) => {
          try {
            // Handle the error properly
            this.loading = false;
            console.error("Error caught:", err); // Log the error
            this.$toast.error(`An error occurred: ${err.message || err}`, {
              position: "bottom-center",
              duration: 30000,
            });
          } catch (catchErr) {
            console.error("Error during error handling:", catchErr);
            this.$toast.error(
              "An unexpected error occurred while handling the error.",
              {
                position: "bottom-center",
                duration: 30000,
              }
            );
          }
        });
    },
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "blue";
        case "confirmed":
          return "green";
        case "declined":
          return "red";
        default:
          return "grey";
      }
    },
  },
};
</script>

<style scoped>
.ml-2 {
  margin-left: 8px;
}
</style>
